// src/cockpit4/components/GeoAcquisitionLoopPanel.tsx
// ============================================
// GEO 获客闭环（P0 核心）：把"监测零件"串成对外获客动作链
//
// 流程（真实后端 /geo-acquisition-loop，无 mock）：
//   ① 泛词缺口识别：选定泛词问题 + AI 平台 → 逐平台真实问答 → 找出"该提却未提"本品牌的问题
//   ② 内容补位：对每个缺口用 DeepSeek 生成官网答案型文章要点 brief（自动写回 Obsidian Vault）
//   ③ 销售线索：从泛词缺口识别高购买意向词 → 输出销售线索卡
//   ④ 月报：输出 AI 可见度月报（交付物）要点
// 核心目标：让 AI 搜泛词时提及"小舍科技 XEAOS"——持续发布补位稿到权威信源（官网/知乎/公众号/百家号）
// ============================================

import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Workflow,
  Play,
  RefreshCw,
  Target,
  FileText,
  Users,
  FileBarChart,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Save,
  MessageSquareText,
} from "lucide-react";
import { DIAG_PLATFORM_MAP } from "../../runtime/GrowthFactory/geoPlatforms";
import DiagPlatformSelector from "./DiagPlatformSelector";
import type { DiagPlatformKey } from "../../runtime/GrowthFactory/types";

type Stage = "idle" | "running" | "done" | "error";

/** 后端返回的数据结构（与 /geo-acquisition-loop 对齐） */
interface LoopResult {
  success: boolean;
  brand: string;
  brandWords: string[];
  results: LoopQuestionResult[];
  briefs: LoopBrief[];
  leads: LoopLead[];
  summary: LoopSummary;
  report: { label: string; items: string[] };
  savedDir: string | null;
}

interface LoopQuestionResult {
  question: string;
  platforms: {
    platform: DiagPlatformKey;
    platformName: string;
    mentioned: boolean;
    first: boolean;
    top3: boolean;
    answer: string;
  }[];
  gap: boolean;
  mentionedCount: number;
}

interface LoopBrief {
  question: string;
  title: string;
  points: string[];
  cta: string;
  savedPath?: string;
  raw?: string;
  error?: string;
}

interface LoopLead {
  question: string;
  intent: string;
  gap: boolean;
  suggest: string;
}

interface LoopSummary {
  score: number;
  mentionRate: number;
  firstRate: number;
  top3Rate: number;
  gapCount: number;
  totalQuestions: number;
  level: string;
  advice: string;
}

const DEFAULT_QUESTIONS = [
  "推荐一下企业AI系统平台？",
  "中小企业怎么做 AI 获客？有没有现成方案？",
  "GEO 获客怎么做？有哪些靠谱的服务商？",
  "酒店自助入住机哪个品牌做得好？",
  "酒店智能化改造怎么落地？",
  "搭建企业AI系统大概要多少钱？",
];

/** 平台色板兜底（与 geoPlatforms 一致） */
const PLATFORM_COLOR: Record<string, string> = {
  doubao: "#3B82F6",
  ernie: "#EF4444",
  qwen: "#8B5CF6",
  yuanbao: "#06B6D4",
  deepseek: "#10B981",
  kimi: "#F59E0B",
};

export default function GeoAcquisitionLoopPanel() {
  const [brand, setBrand] = useState("小舍科技");
  const [questionsText, setQuestionsText] = useState(DEFAULT_QUESTIONS.join("\n"));
  const [selected, setSelected] = useState<DiagPlatformKey[]>([
    "deepseek", "doubao", "qwen", "ernie",
  ]);
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState<LoopResult | null>(null);
  const [error, setError] = useState("");
  const abortRef = useRef(false);

  const questions = useMemo(
    () => questionsText.split("\n").map((s) => s.trim()).filter(Boolean),
    [questionsText]
  );

  const runLoop = async () => {
    const qs = questions;
    if (qs.length === 0) {
      setError("请至少输入 1 个泛词问题");
      setStage("error");
      return;
    }
    if (selected.length === 0) {
      setError("请至少勾选 1 个 AI 平台");
      setStage("error");
      return;
    }
    abortRef.current = false;
    setError("");
    setResult(null);
    setStage("running");
    setProgress("正在向各 AI 平台发起泛词问答，识别品牌缺口…");
    try {
      const resp = await fetch("/geo-acquisition-loop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brand: brand.trim(), questions: qs, platforms: selected }),
      });
      const data: LoopResult = await resp.json();
      if (!resp.ok || !data?.success) {
        throw new Error((data as any)?.error || `请求失败(${resp.status})`);
      }
      if (abortRef.current) return;
      setResult(data);
      setStage("done");
    } catch (e: any) {
      if (abortRef.current) return;
      setError(e?.message || String(e));
      setStage("error");
    }
  };

  const s = result?.summary;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* 顶栏 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Workflow size={16} className="text-fuchsia-400" />
            <span className="text-[13px] font-semibold text-white tracking-tight">
              GEO 获客闭环
            </span>
          </div>
          <p className="text-[10px] font-mono text-gray-500 mt-1">
            泛词缺口识别 → 内容补位稿 → 销售线索卡 → AI可见度月报（真实多平台问答，无模拟数据）
          </p>
        </div>
      </div>

      {/* 输入区 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col gap-2.5">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-mono text-gray-500">品牌 / 公司（用于识别 AI 是否提及）</label>
          <input
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="例如：小舍科技"
            className="flex-1 bg-black/20 border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-gray-600 focus:outline-none focus:border-fuchsia-500/50"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-mono text-gray-500">
              泛词问题池（每行 1 个，最多 8 个）
            </label>
            <span className="text-[9px] font-mono text-gray-600">{questions.length} 个问题</span>
          </div>
          <textarea
            value={questionsText}
            onChange={(e) => setQuestionsText(e.target.value)}
            rows={5}
            className="flex-1 bg-black/20 border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-gray-600 focus:outline-none focus:border-fuchsia-500/50 resize-none leading-relaxed"
          />
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={runLoop}
            disabled={stage === "running"}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-medium
                       bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30
                       hover:bg-fuchsia-500/25 disabled:opacity-50 transition-colors"
          >
            {stage === "running" ? (
              <RefreshCw size={13} className="animate-spin" />
            ) : (
              <Play size={13} />
            )}
            {stage === "running" ? "闭环执行中…" : "启动获客闭环"}
          </motion.button>
          {stage === "running" && (
            <button
              onClick={() => { abortRef.current = true; setStage("idle"); setProgress(""); }}
              className="text-[10px] text-gray-500 hover:text-gray-300 px-2 py-1 rounded border border-white/[0.06]"
            >
              停止
            </button>
          )}
        </div>
        {error && <div className="text-[11px] text-red-400">{error}</div>}
      </div>

      {/* 平台勾选区（复用诊断平台选择器） */}
      <DiagPlatformSelector selected={selected} onChange={setSelected} />

      {progress && (
        <div className="text-[11px] font-mono text-fuchsia-300/80">
          <RefreshCw size={12} className="inline mr-1 animate-spin" />
          <span className="text-fuchsia-400">▸</span> {progress}
        </div>
      )}

      {stage === "idle" && (
        <div className="text-[11px] text-gray-500 py-8 text-center border border-dashed border-white/[0.06] rounded-lg">
          输入品牌 + 泛词问题池，勾选 AI 平台，点击「启动获客闭环」。
          系统会真实询问各 AI，找出<b className="text-gray-300">「该提却未提」小舍科技</b>的泛词缺口，
          并自动生成补位稿、销售线索卡与可见度月报。
        </div>
      )}

      {result && s && (
        <>
          {/* 汇总指标 */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-fuchsia-400">{s.score}</div>
              <div className="text-[11px] text-gray-300 mt-0.5">AI 可见度得分</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">{s.level}</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-emerald-400">{s.mentionRate}%</div>
              <div className="text-[11px] text-gray-300 mt-0.5">提及率</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">AI 回答中提及品牌</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-amber-400">{s.top3Rate}%</div>
              <div className="text-[11px] text-gray-300 mt-0.5">前 3 占比</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">回答前段即出现</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-cyan-400">{s.firstRate}%</div>
              <div className="text-[11px] text-gray-300 mt-0.5">首条占比</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">开头即推荐品牌</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-red-400">{s.gapCount}</div>
              <div className="text-[11px] text-gray-300 mt-0.5">泛词缺口</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">/{s.totalQuestions} 个问题未被提及</div>
            </div>
          </div>

          {/* 策略建议 */}
          <div className="rounded-lg bg-white/[0.02] border border-fuchsia-500/20 p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <TrendingUp size={13} className="text-fuchsia-400" />
              <span className="text-[11px] font-semibold text-white">策略建议</span>
            </div>
            <div className="text-[11px] text-gray-300 leading-relaxed">{s.advice}</div>
            <div className="text-[9px] font-mono text-gray-600 mt-1">
              提及词：{result.brandWords.join(" / ")}
            </div>
          </div>

          {/* ① 泛词缺口清单 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-3">
              <Target size={14} className="text-red-400" />
              <span className="text-[12px] font-semibold text-white">① 泛词缺口识别</span>
              <span className="text-[9px] font-mono text-gray-600">该提却未提 · 逐平台真实问答结果</span>
            </div>
            <div className="flex flex-col gap-2">
              {result.results.map((r) => (
                <div
                  key={r.question}
                  className={`rounded-md border p-2.5 ${
                    r.gap
                      ? "bg-red-500/[0.04] border-red-500/20"
                      : "bg-emerald-500/[0.03] border-emerald-500/15"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <MessageSquareText size={12} className="text-gray-400 shrink-0" />
                    <span className="text-[11px] font-medium text-white flex-1 leading-snug">{r.question}</span>
                    {r.gap ? (
                      <span className="flex items-center gap-1 text-[10px] text-red-400 shrink-0">
                        <XCircle size={11} /> 缺口（{r.mentionedCount} 平台提及）
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-400 shrink-0">
                        <CheckCircle2 size={11} /> 已提及（{r.mentionedCount} 平台）
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {r.platforms.map((p) => {
                      const meta = DIAG_PLATFORM_MAP[p.platform];
                      const color = meta?.color || PLATFORM_COLOR[p.platform] || "#888";
                      return (
                        <span
                          key={p.platform}
                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${
                            p.mentioned
                              ? "text-white border-white/[0.1] bg-white/[0.04]"
                              : "text-gray-600 border-white/[0.05] bg-white/[0.01]"
                          }`}
                          title={p.answer?.slice(0, 300) || "该平台无回答"}
                        >
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color, opacity: p.mentioned ? 1 : 0.3 }} />
                          {meta?.short || p.platform}
                          {p.mentioned && (p.first ? "·首条" : p.top3 ? "·前3" : "·提及")}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ② 内容补位稿 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <FileText size={14} className="text-indigo-400" />
              <span className="text-[12px] font-semibold text-white">② 内容补位稿（缺口 → 发布动作）</span>
              <span className="text-[9px] font-mono text-gray-600">DeepSeek 生成 · 自动写入知识库</span>
            </div>
            <div className="text-[9px] font-mono text-gray-600 mb-2">
              发布渠道建议：官网 blog → 知乎 → 公众号 → 百家号（权威信源让 AI 语料把「小舍科技 XEAOS」与答案绑定）
            </div>
            {result.briefs.length === 0 ? (
              <div className="text-[11px] text-gray-500 py-3 text-center">
                本次无缺口，无需补位。保持每周补新内容并复验。
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {result.briefs.map((b) => (
                  <div key={b.question} className="rounded-md bg-black/20 border border-white/[0.06] p-2.5">
                    <div className="text-[9px] font-mono text-gray-600 mb-0.5">针对缺口：{b.question}</div>
                    {b.title ? (
                      <div className="text-[12px] font-semibold text-white mb-1.5">📄 {b.title}</div>
                    ) : (
                      <div className="text-[11px] text-amber-400/80 mb-1.5">生成失败：{b.error || "未返回标题"}</div>
                    )}
                    <ul className="flex flex-col gap-1 mb-1.5">
                      {(b.points || []).map((pt, i) => (
                        <li key={i} className="text-[11px] text-gray-300 leading-relaxed">
                          <span className="text-indigo-400 mr-1">▸</span>{pt}
                        </li>
                      ))}
                    </ul>
                    {b.cta && (
                      <div className="text-[10px] text-fuchsia-300/90">CTA：{b.cta}</div>
                    )}
                    {b.savedPath && (
                      <div className="flex items-center gap-1 text-[9px] font-mono text-gray-500 mt-1">
                        <Save size={10} />
                        {b.savedPath}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ③ 销售线索卡 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-amber-400" />
              <span className="text-[12px] font-semibold text-white">③ 销售线索卡（高意向泛词）</span>
              <span className="text-[9px] font-mono text-gray-600">识别带购买意图的泛词缺口 → 可直接转化</span>
            </div>
            {result.leads.length === 0 ? (
              <div className="text-[11px] text-gray-500 py-3 text-center">
                当前问题池中没有命中高购买意向词，可补充「怎么选 / 哪家 / 多少钱」类问题。
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {result.leads.map((l) => (
                  <div
                    key={l.question}
                    className={`flex items-start gap-2 rounded-md border p-2.5 ${
                      l.gap
                        ? "bg-amber-500/[0.04] border-amber-500/25"
                        : "bg-white/[0.01] border-white/[0.06]"
                    }`}
                  >
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20 shrink-0 mt-0.5">
                      高意向
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-medium text-white">{l.question}</div>
                      <div className={`text-[10px] mt-0.5 ${l.gap ? "text-red-300/80" : "text-emerald-300/80"}`}>
                        {l.suggest}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ④ AI 可见度月报 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-3">
              <FileBarChart size={14} className="text-cyan-400" />
              <span className="text-[12px] font-semibold text-white">④ {result.report?.label || "AI 可见度月报"}</span>
              <span className="text-[9px] font-mono text-gray-600">标价交付物 · 可导出给客户</span>
            </div>
            <div className="flex flex-col gap-1.5">
              {(result.report?.items || []).map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] text-gray-300 leading-relaxed">
                  <CheckCircle2 size={12} className="text-cyan-400 mt-0.5 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="text-[9px] font-mono text-gray-600 mt-2">
              {result.savedDir ? `补位稿已写入知识库：${result.savedDir}` : "本次无补位稿写入"}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
